import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, money, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import { ClockClockwise, Plus, Trash, Warning } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";

const empty = { entry_date: format(new Date(), "yyyy-MM-dd"), hours: "" };

export default function RegularHours() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const currency = user?.currency || "CAD";
  const baseRate = user?.base_hourly_rate || 0;

  const { data: entries = [] } = useQuery({
    queryKey: ["regular-hours"],
    queryFn: async () => (await api.get("/regular-hours")).data,
  });

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post("/regular-hours", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["regular-hours"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["projections"] });
      toast.success("Regular hours added.");
      setForm(empty);
      setOpen(false);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => api.delete(`/regular-hours/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["regular-hours"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["projections"] });
      toast.success("Entry removed.");
    },
  });

  const submit = (e) => {
    e.preventDefault();
    createMut.mutate({ entry_date: form.entry_date, hours: parseFloat(form.hours) || 0 });
  };

  const total = entries.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div className="animate-fade-up" data-testid="regular-hours-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="overline text-blue-400">Module</p>
          <h1 className="text-3xl font-black tracking-tight mt-1">Regular Hours</h1>
          <p className="text-muted-foreground text-sm mt-1">Standard shift hours paid at your base rate.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-regular-button" className="rounded-md font-semibold">
              <Plus className="h-4 w-4 mr-1.5" weight="bold" /> Add Entry
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-popover border-border" data-testid="regular-dialog">
            <DialogHeader><DialogTitle>Log Regular Hours</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Date</Label>
                <div className="mt-1.5"><DatePicker testId="regular-date" value={form.entry_date}
                  onChange={(d) => setForm({ ...form, entry_date: d })} /></div>
              </div>
              <div>
                <Label>Hours</Label>
                <Input type="number" step="0.25" min="0" data-testid="regular-hours-input" className="mt-1.5 bg-secondary/50" required
                  value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="8" />
              </div>
              <div className="text-sm text-muted-foreground bg-secondary/40 rounded-md p-3">
                {baseRate ? <>Estimated: <span className="text-foreground font-semibold">
                  {money((parseFloat(form.hours) || 0) * baseRate, currency)}</span> at {money(baseRate, currency)}/hr</>
                  : "Set your base hourly rate in Settings first."}
              </div>
              <DialogFooter>
                <Button type="submit" disabled={createMut.isPending} data-testid="regular-submit" className="rounded-md font-semibold">
                  {createMut.isPending ? "Saving…" : "Save Entry"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {!baseRate && (
        <Card className="p-4 mb-6 bg-warning/10 border-warning/30 flex items-center gap-3" data-testid="regular-no-rate">
          <Warning className="h-5 w-5 text-warning shrink-0" weight="fill" />
          <span className="text-sm">Set your base hourly rate in{" "}
            <Link to="/settings" className="text-primary font-semibold hover:underline">Settings</Link>{" "}to calculate pay.</span>
        </Card>
      )}

      {entries.length === 0 ? (
        <Card className="p-12 bg-card border-border text-center" data-testid="regular-empty">
          <ClockClockwise className="h-10 w-10 text-muted-foreground mx-auto mb-3" weight="bold" />
          <p className="text-muted-foreground">No regular hours logged yet.</p>
        </Card>
      ) : (
        <Card className="bg-card border-border overflow-hidden">
          <div className="hidden sm:grid grid-cols-12 gap-2 px-5 py-3 border-b border-border overline text-muted-foreground">
            <span className="col-span-5">Date</span><span className="col-span-3">Hours</span>
            <span className="col-span-2">Rate</span><span className="col-span-2 text-right">Amount</span>
          </div>
          {entries.map((e) => (
            <div key={e.id} data-testid={`regular-row-${e.id}`}
              className="grid grid-cols-2 sm:grid-cols-12 gap-2 px-5 py-4 border-b border-border last:border-0 items-center hover:bg-secondary/30 transition-colors duration-200">
              <span className="col-span-1 sm:col-span-5 font-medium">{format(new Date(e.entry_date + "T00:00:00"), "MMM d, yyyy")}</span>
              <span className="hidden sm:block col-span-3 text-muted-foreground">{e.hours}h</span>
              <span className="hidden sm:block col-span-2 text-muted-foreground">{money(e.base_rate, currency)}</span>
              <div className="col-span-1 sm:col-span-2 flex items-center justify-end gap-3">
                <span className="font-bold text-blue-400">{money(e.amount, currency)}</span>
                <button data-testid={`delete-regular-${e.id}`} onClick={() => deleteMut.mutate(e.id)}
                  className="text-muted-foreground hover:text-destructive transition-colors duration-200">
                  <Trash className="h-4 w-4" weight="bold" />
                </button>
              </div>
            </div>
          ))}
          <div className="flex justify-between px-5 py-4 bg-secondary/30">
            <span className="font-semibold">Total Regular</span>
            <span className="font-black text-blue-400" data-testid="regular-total">{money(total, currency)}</span>
          </div>
        </Card>
      )}
    </div>
  );
}
